import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageSquare, Instagram, Facebook } from "lucide-react";
import Logo from "@/components/Logo";
import { FOOTER } from "@/constants/testIds";

export default function Footer({ settings }) {
  const phoneClean = (settings?.phone || "+1 (619) 769-2398").replace(/[^+\d]/g, "");
  const smsClean = (settings?.sms_phone || "+1 (619) 712-3970").replace(/[^+\d]/g, "");

  return (
    <footer
      id="footer"
      data-testid={FOOTER.root}
      className="relative bg-black border-t border-white/10"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Logo logoUrl={settings?.logo_url} size="md" />
          <p className="mt-6 text-zinc-400 max-w-md leading-relaxed">
            MSJ Car Detailing &amp; Pressure Washing — premium detailing and
            exterior cleaning services in Mandeville, Louisiana. Family-owned,
            obsessed with results.
          </p>
          <div className="flex items-center gap-3 mt-6">
            {settings?.instagram_url && (
              <a
                href={settings.instagram_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-cyan-400 hover:text-cyan-400 text-zinc-300 transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
            )}
            {settings?.facebook_url && (
              <a
                href={settings.facebook_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/10 flex items-center justify-center hover:border-cyan-400 hover:text-cyan-400 text-zinc-300 transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
            )}
          </div>
        </div>

        <div>
          <h4 className="uppercase tracking-widest text-xs text-cyan-400 mb-6">Services</h4>
          <ul className="space-y-3 text-sm text-zinc-400">
            <li>Auto Detailing</li>
            <li>Ceramic Coatings</li>
            <li>Pressure Washing</li>
            <li>House &amp; Driveway</li>
            <li>Fence &amp; Deck</li>
          </ul>
        </div>

        <div>
          <h4 className="uppercase tracking-widest text-xs text-cyan-400 mb-6">Contact</h4>
          <ul className="space-y-4 text-sm text-zinc-300">
            <li>
              <a href={`tel:${phoneClean}`} className="flex items-center gap-2 hover:text-cyan-400">
                <Phone className="w-4 h-4" />
                {settings?.phone || "+1 (619) 769-2398"}
              </a>
            </li>
            {settings?.sms_phone && (
              <li>
                <a href={`sms:${smsClean}`} className="flex items-center gap-2 hover:text-cyan-400">
                  <MessageSquare className="w-4 h-4" />
                  {settings.sms_phone}
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 ml-1">Text only</span>
                </a>
              </li>
            )}
            {settings?.email && (
              <li>
                <a href={`mailto:${settings.email}`} className="flex items-center gap-2 hover:text-cyan-400">
                  <Mail className="w-4 h-4" />
                  {settings.email}
                </a>
              </li>
            )}
            <li className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{settings?.address || "1705 Hwy 59, Mandeville, LA"}</span>
            </li>
            {settings?.hours && (
              <li className="text-xs text-zinc-500 uppercase tracking-widest whitespace-pre-line pl-6">
                {settings.hours}
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-5 md:px-10 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-zinc-500">
          <div>
            © {new Date().getFullYear()} MSJ Car Detailing &amp; Pressure Washing. All rights reserved.
          </div>
          <Link
            to="/admin/login"
            data-testid={FOOTER.adminLink}
            className="hover:text-cyan-400 uppercase tracking-widest"
          >
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
